import React, { Component } from 'react';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { getContentHandler } from './components/courseContent';
import Exam from './Exam';
import ScenarioTraining from './ScenarioTraining';
import { Modal, Button } from 'react-bootstrap';
import { setTrainingTelemetryContext, trackTrainingEvent } from './trainingTelemetry';
import { getMaterialsFromCourseData } from './trainingMaterials';

class Training extends Component {
  state = {
    courseData: null,
    materials: [],
    materialIndex: 0,
    isLoading: true,
    error: null,
    isExamStarted: false,
    showModal: false
  };

  componentDidMount() {
    this.fetchCourseData();
  }

  // Build the ordered list of materials the learner must go through before the exam.
  // Shared with RegistrationStation.js (src/trainingMaterials.js) — jedna funkcja, zeby obie
  // sciezki (logowanie pracownika / stanowisko rejestracyjne) nigdy sie nie rozjechaly.
  getMaterials = (courseData) => getMaterialsFromCourseData(courseData);

  fetchCourseData = () => {
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    axios.get(`${apiUrl}${this.props.trainingId}`)
      .then(response => {
        const courseData = response.data.training;
        const materials = this.getMaterials(courseData);
        setTrainingTelemetryContext({ trainingId: courseData.id, userUuid: this.props.userId });
        trackTrainingEvent('course_loaded', 'course-data', 'Course data loaded', {
          trainingId: courseData.id,
          materialsCount: materials.length,
          questionsCount: Array.isArray(courseData.questions) ? courseData.questions.length : 0,
          scenarioEnabled: Boolean(courseData.scenario && courseData.scenario.enabled),
        });
        // Auto-start exam only when there is no material at all (test-only training)
        this.setState({
          courseData,
          materials,
          materialIndex: 0,
          isLoading: false,
          isExamStarted: materials.length === 0
        });
      })
      .catch(error => {
        this.setState({ error: error.message, isLoading: false, showModal: true });
      });
  };

  // Called when the current material is finished (video watched / last PDF page).
  // Advance to the next material, or start the exam once the last one is done.
  handleContentContinue = () => {
    const { materialIndex, materials } = this.state;
    const material = materials[materialIndex] || {};
    trackTrainingEvent('material_done', 'course-material', 'Course material completed', {
      materialIndex,
      materialsCount: materials.length,
      materialUrl: material.url || null,
    });
    if (materialIndex < materials.length - 1) {
      this.setState({ materialIndex: materialIndex + 1 });
    } else {
      trackTrainingEvent('exam_start', 'exam-start', 'Exam started', {
        trainingId: this.props.trainingId,
      });
      this.setState({ isExamStarted: true });
    }
  };

  handleRetakeCourse = () => {
    trackTrainingEvent('course_retake', 'course-retake', 'Course retake', {
      trainingId: this.props.trainingId,
    });
    this.setState({
      isExamStarted: false,
      courseData: null,
      materials: [],
      materialIndex: 0,
      isLoading: true
    }, () => {
      this.fetchCourseData();
    });
  };

  handleCloseModal = () => {
    this.setState({ showModal: false });
  };

  // Scenario mode ("step-by-step"): backend sends `scenario.steps` when the
  // admin configured a step-by-step training (fragment + mini-test per step).
  // When absent/null the training has no scenario configured — fall through
  // to the pre-existing materials[] -> single Exam flow, unchanged.
  hasScenario = (courseData) => {
    return !!(courseData
      && courseData.scenario
      && courseData.scenario.enabled
      && Array.isArray(courseData.scenario.steps)
      && courseData.scenario.steps.length > 0);
  };

  render() {
    const { t } = this.props;
    const { courseData, isLoading, error, isExamStarted, showModal } = this.state;

    if (isLoading) {
      return <div>{t('loading_course_data')}</div>;
    }

    if (courseData && this.hasScenario(courseData)) {
      return (
        <div className="Training vh-100 d-flex flex-column">
          <div className="flex-grow-1">
            <ScenarioTraining
              courseData={courseData}
              scenario={courseData.scenario}
              trainingId={courseData.id}
              userId={this.props.userId}
              proof={this.props.proof}
              isPreview={this.props.isPreview}
              onRetakeCourse={this.handleRetakeCourse}
              startNewTraining={this.props.startNewTraining}
              returnUrl={this.props.returnUrl}
            />
          </div>

          <Modal show={showModal} onHide={this.handleCloseModal}>
            <Modal.Header closeButton>
              <Modal.Title>{t('error')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{error}</Modal.Body>
            <Modal.Footer>
              <Button variant="danger" onClick={this.handleCloseModal}>
                {t('close')}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      );
    }

    return (
      <div className="Training vh-100 d-flex flex-column">
        {courseData && (
          <div className="flex-grow-1">
            {!isExamStarted ? (
              (() => {
                const { materials, materialIndex } = this.state;
                const material = materials[materialIndex];
                if (!material || !material.url) return null;
                const ContentHandler = getContentHandler(material.url);
                // key forces a fresh mount per step so each material resets its own
                // internal state (video progress / PDF page) when we advance.
                return (
                  <ContentHandler
                    key={materialIndex}
                    content={material}
                    onExamStart={this.handleContentContinue}
                    isLastMaterial={materialIndex >= materials.length - 1}
                    showSeekControls={courseData.showSeekControls}
                  />
                );
              })()
            ) : (
              <div className="d-flex justify-content-center align-items-center h-100">
                <Exam
                  questions={courseData.questions}
                  trainingId={courseData.id}
                  userId={this.props.userId}
                  proof={this.props.proof}
                  isPreview={this.props.isPreview}
                  requiredQuestionsCount={courseData.requiredQuestionsCount}
                  requiredPointsPercentage={courseData.requiredPointsPercentage}
                  onRetakeCourse={this.handleRetakeCourse}
                  startNewTraining={this.props.startNewTraining}
                  returnUrl={this.props.returnUrl}
                />
              </div>
            )}
          </div>
        )}

        <Modal show={showModal} onHide={this.handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>{t('error')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{error}</Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={this.handleCloseModal}>
              {t('close')}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}

export default withTranslation()(Training);
